import { useEventBus } from '@/composables/useEventBus';
import type { OnboardingStepDeps } from '@/composables/useOnboarding';
import i18n from '@/i18n';
import { autoStyleChapter } from '@/onboarding/autoStyles';
import type { StepOptions } from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { nextTick } from 'vue';
import type { Composer } from 'vue-i18n';
import { createActionStep, createInfoStep } from '../stepStyles';

const t = (key: string, params: Record<string, unknown> = {}) => (i18n.global as unknown as Composer).t(key, params);

const eventBus = useEventBus();

export function getPlanningSteps({ tour, router, waitForElement }: OnboardingStepDeps): StepOptions[] {
  const steps: StepOptions[] = [
    // 20.5 - Transition vers l'accueil
    createInfoStep({
      id: 'transition-to-planning',
      title: t('onboarding.chapters.planning.steps.transition-to-planning.title'),
      text: t('onboarding.chapters.planning.steps.transition-to-planning.text'),
      canClickTarget: false,
      buttons: [
        {
          text: t('onboarding.chapters.planning.steps.transition-to-planning.buttonLabel'),
          action: async () => {
            await router.push({ path: '/' });
            await nextTick();
            tour.value?.next();
          },
        },
      ],
    }),
    // 21 — Planning
    createActionStep(
      {
        id: 'planning-link',
        title: t('onboarding.chapters.planning.steps.planning-link.title'),
        text: t('onboarding.chapters.planning.steps.planning-link.text'),
        canClickTarget: true,
        advanceOn: { selector: '.onboarding-planning', event: 'click' },
        beforeShowPromise: async () => {
          await waitForElement('.onboarding-planning', 10000);
          await nextTick();
        },
        attachTo: { element: '.onboarding-planning', on: 'top' },
      },
      {
        placement: 'left',
      },
    ),
    // 21.1 - Découverte Menu semaine
    createInfoStep({
      id: 'weekly-menu',
      title: t('onboarding.chapters.planning.steps.weekly-menu.title'),
      text: t('onboarding.chapters.planning.steps.weekly-menu.text'),
      attachTo: { element: '.onboarding-tab-week-menu', on: 'top' },
      canClickTarget: false,
      extraHighlights: ['[data-pc-section="navcontainer"]'],
      beforeShowPromise: async () => {
        eventBus.emit('onboarding:set-planning-tab', 0);
        await waitForElement('.onboarding-tab-week-menu', 10000);
        await nextTick();
      },
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        { text: t('onboarding.common.next'), action: () => tour.value?.next() },
      ],
    }),
    createActionStep(
      {
        id: 'action-goto-planning-presence',
        title: t('onboarding.chapters.planning.steps.action-goto-planning-presence.title'),
        text: t('onboarding.chapters.planning.steps.action-goto-planning-presence.text', {
          dynamicText: `<strong>${t('onboarding.bold.chapter_8.presences')}</strong>`,
        }),
        attachTo: { element: '#onboarding-tab-presences-button', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '#onboarding-tab-presences-button', event: 'click' },
        beforeShowPromise: async () => {
          await waitForElement('#onboarding-tab-presences-button', 10000);
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),
    // 22 - Découverte présences équipe
    createInfoStep({
      id: 'planning-presence',
      title: t('onboarding.chapters.planning.steps.planning-presence.title'),
      text: t('onboarding.chapters.planning.steps.planning-presence.text'),
      attachTo: { element: '.onboarding-tab-presences', on: 'top' },
      canClickTarget: false,
      beforeShowPromise: async () => {
        eventBus.emit('onboarding:set-planning-tab', 1);
        await waitForElement('.onboarding-tab-presences', 10000);
        await nextTick();
      },
      extraHighlights: ['[data-pc-section="navcontainer"]'],
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        { text: t('onboarding.common.next'), action: () => tour.value?.next() },
      ],
    }),
    createActionStep(
      {
        id: 'action-goto-planning-task',
        title: t('onboarding.chapters.planning.steps.action-goto-planning-task.title'),
        text: t('onboarding.chapters.planning.steps.action-goto-planning-task.text', {
          dynamicText: `<strong>${t('onboarding.bold.chapter_8.planning')}</strong>`,
        }),
        attachTo: { element: '#onboarding-tab-tasks-button', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '#onboarding-tab-tasks-button', event: 'click' },
        beforeShowPromise: async () => {
          await waitForElement('#onboarding-tab-tasks-button', 10000);
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),
    // 23 - Découverte des tâches
    createInfoStep({
      id: 'planning-tasks',
      title: t('onboarding.chapters.planning.steps.planning-tasks.title'),
      text: t('onboarding.chapters.planning.steps.planning-tasks.text'),
      attachTo: { element: '.onboarding-tab-tasks', on: 'top' },
      canClickTarget: false,
      beforeShowPromise: async () => {
        eventBus.emit('onboarding:set-planning-tab', 2);
        await waitForElement('.onboarding-tab-tasks', 10000);
        await nextTick();
      },
      extraHighlights: ['[data-pc-section="navcontainer"]'],
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        {
          text: t('onboarding.common.finish'),
          action: async () => {
            router.back();
            await nextTick();
            tour.value?.next();
          },
        },
      ],
    }),
  ];

  // Appliquer automatiquement les styles appropriés
  return autoStyleChapter(steps);
}
